import * as d3 from 'd3';
import { jsonParse } from '@/libs/tools';
import i18n from '@/locales/i18n';

// Use global i18n instance for translations
const t = key => {
  return i18n.global.t(key);
};

class ShareHoldingComponent extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: relative;
          width: 100%;
          overflow-x: auto;
          overflow-y: visible;
        }
        .borrow {
          position: relative;
          width: 100%;
          overflow-x: auto;
          overflow-y: visible;
        }
        .functionBox {
          padding: 5px;
          position: absolute;
          right: 10px;
          bottom: 45%;
          display: flex;
          flex-direction: column;
          background: #f8f8f8;
          transition: all linear 0.2s;
          border-radius: 8px;
        }
        .functionItem {
          color: #444;
          height: 35px;
          width: 35px;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          border-radius: 4px;
        }
        .functionItem:hover {
          background: rgb(233, 233, 233);
        }
      </style>
      <div class="borrow">
        <div id="chart-container"></div>
      </div>
    `;

    this.chartContainer = this.shadowRoot.getElementById('chart-container');
    // Remove ID usage to avoid Shadow DOM issues
    // this.id = `shareholding-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    this.config = {
      dx: 200, // Horizontal spacing
      dy: 120, // Vertical spacing, reduced to avoid excessive height
      width: 0,
      height: 650,
      rectWidth: 170,
      rectHeight: 70
    };
    this.svgs = null;
    this.gAlls = null;
    this.gLinks = null;
    this.gNodes = null;
    this.tree = null;
    this.rootOfDown = null;
    this.rootOfUp = null;
    this.resizeObserver = null;
  }

  static get observedAttributes() {
    return ['data', 'width', 'height'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data' && newValue) {
      this.chartData = jsonParse(newValue);
      this.drawChart({ type: 'all' });
    } else if (name === 'width' || name === 'height') {
      this.updateSize();
    }
  }

  connectedCallback() {
    // Initialize with default data if available
    const dataAttr = this.getAttribute('data');
    if (dataAttr) {
      this.chartData = jsonParse(dataAttr);
      this.drawChart({ type: 'all' });
    }

    // Set up resize observer
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateSize();
      });
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clean up D3 elements
    if (this.svgs) {
      this.svgs.remove();
    }
  }

  updateSize() {
    const width = this.getAttribute('width') || '100%';
    this.chartContainer.style.width = width;
    // Don't set height here - it will be set based on SVG height after rendering

    // Redraw chart with new dimensions
    if (this.chartData) {
      this.drawChart({ type: 'all' });
    }
  }

  // Helper method to update container height based on SVG height
  updateContainerHeight() {
    if (this.svgs && this.svgs.node()) {
      const svgElement = this.svgs.node();
      const svgRect = svgElement.getBoundingClientRect();
      if (svgRect.height > 0) {
        this.chartContainer.style.height = `${svgRect.height}px`;
      }
    }
  }

  // Calculate the maximum depth of the tree
  getMaxDepth(node, childrenKey) {
    if (!node || !node[childrenKey] || node[childrenKey].length === 0) {
      return 1;
    }

    let maxDepth = 1;
    for (const child of node[childrenKey]) {
      const childDepth = this.getMaxDepth(child, childrenKey);
      maxDepth = Math.max(maxDepth, childDepth + 1);
    }
    return maxDepth;
  }

  drawChart(options) {
    if (!this.chartData) return;

    const host = d3.select(this.chartContainer);
    const dom = host.node();
    const domRect = dom.getBoundingClientRect();
    this.config.width = domRect.width || 800;

    // Calculate height based on tree depth and node spacing
    const maxDownDepth = this.getMaxDepth(this.chartData, 'children');
    const maxUpDepth = this.getMaxDepth(this.chartData, 'parents');
    const totalDepth = maxDownDepth + maxUpDepth - 1;

    const estimatedHeight = Math.max(400, totalDepth * this.config.dy + 100);
    this.config.height = estimatedHeight;

    // Remove existing SVG
    const oldSvg = host.select('svg');
    if (!oldSvg.empty()) {
      oldSvg.remove();
    }

    try {
      // Create SVG element in shadow DOM context
      const svgElement = this.shadowRoot.ownerDocument.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      );

      const svg = d3
        .select(svgElement)
        .style('width', '100%')
        .style('display', 'block')
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('user-select', 'none')
        .style('cursor', 'move');

      const gAll = svg.append('g');

      svg
        .call(
          d3
            .zoom()
            .scaleExtent([0.1, 2])
            .on('zoom', e => {
              gAll.attr('transform', () => {
                return `translate(${e.transform.x},${e.transform.y}) scale(${e.transform.k})`;
              });
            })
        )
        .on('dblclick.zoom', null);

      this.gAlls = gAll;
      this.gLinks = gAll.append('g');
      this.gNodes = gAll.append('g');
      this.tree = d3.tree().nodeSize([this.config.dx, this.config.dy]);
      this.rootOfDown = d3.hierarchy(this.chartData, d => d.children);
      this.rootOfUp = d3.hierarchy(this.chartData, d => d.parents);

      // Handle expand/collapse state of nodes
      [this.rootOfDown.descendants(), this.rootOfUp.descendants()].forEach(
        nodes => {
          nodes.forEach(node => {
            node._children = node.children || null;

            if (options.type === 'all') {
              node.children = node._children;
            } else if (options.type === 'fold') {
              if (node.depth > 0) {
                node.children = null;
              }
            }
          });
        }
      );

      // Calculate tree layout
      this.tree(this.rootOfDown);
      this.tree(this.rootOfUp);

      // Calculate boundaries and set viewBox
      const allNodes = [
        ...this.rootOfDown.descendants(),
        ...this.rootOfUp.descendants().map(d => ({ ...d, y: -d.y }))
      ];

      if (allNodes.length > 0) {
        const xExtent = d3.extent(allNodes, d => d.x);
        const yExtent = d3.extent(allNodes, d => d.y);

        const padding = 50;
        const actualWidth =
          xExtent[1] - xExtent[0] + this.config.rectWidth + padding * 2;
        const actualHeight =
          yExtent[1] - yExtent[0] + this.config.rectHeight + padding * 2;

        const viewBoxX = xExtent[0] - this.config.rectWidth / 2 - padding;
        const viewBoxY = yExtent[0] - this.config.rectHeight / 2 - padding;

        svg.attr(
          'viewBox',
          [viewBoxX, viewBoxY, actualWidth, actualHeight].join(' ')
        );
      }

      // Add markers
      try {
        svg
          .append('marker')
          .attr('id', 'markerOfDown')
          .attr('markerUnits', 'userSpaceOnUse')
          .attr('viewBox', '0 -5 10 10')
          .attr('refX', 55)
          .attr('refY', 0)
          .attr('markerWidth', 10)
          .attr('markerHeight', 10)
          .attr('orient', '90')
          .attr('stroke-width', 2)
          .append('path')
          .attr('d', 'M0,-5L10,0L0,5')
          .attr('fill', '#215af3');

        svg
          .append('marker')
          .attr('id', 'markerOfUp')
          .attr('markerUnits', 'userSpaceOnUse')
          .attr('viewBox', '0 -5 10 10')
          .attr('refX', -50)
          .attr('refY', 0)
          .attr('markerWidth', 10)
          .attr('markerHeight', 10)
          .attr('orient', '90')
          .attr('stroke-width', 2)
          .append('path')
          .attr('d', 'M0,-5L10,0L0,5')
          .attr('fill', '#215af3');
      } catch (error) {
        console.error('❌ Error creating markers:', error);
        throw error;
      }

      this.svgs = svg;
      this.update();

      this.chartContainer.appendChild(svgElement);

      // Set container height to match SVG height after rendering
      setTimeout(() => {
        this.updateContainerHeight();
      }, 100);
    } catch (error) {
      console.error('❌ Error in drawChart:', error);
      console.error('Stack trace:', error.stack);
      throw error;
    }
  }

  update(source) {
    try {
      if (!source) {
        source = { x0: 0, y0: 0 };
        this.rootOfDown.x0 = 0;
        this.rootOfDown.y0 = 0;
        this.rootOfUp.x0 = 0;
        this.rootOfUp.y0 = 0;
      }

      const nodesOfDown = this.rootOfDown.descendants().reverse();
      const linksOfDown = this.rootOfDown.links();
      const nodesOfUp = this.rootOfUp.descendants().reverse();
      const linksOfUp = this.rootOfUp.links();

      this.tree(this.rootOfDown);
      this.tree(this.rootOfUp);

      const myTransition = this.svgs.transition().duration(500);

      // Draw downstream nodes
      this.drawNodes(
        nodesOfDown,
        linksOfDown,
        'nodeOfDownItemGroup',
        'linkOfDownItem',
        'markerOfDown',
        source,
        myTransition
      );

      // Draw upstream nodes
      nodesOfUp.forEach(node => {
        node.y = -node.y;
      });
      this.drawNodes(
        nodesOfUp,
        linksOfUp,
        'nodeOfUpItemGroup',
        'linkOfUpItem',
        'markerOfUp',
        source,
        myTransition
      );

      // Update expand buttons
      const expandButtonsSelection = d3.selectAll('g.expandBtn');
      expandButtonsSelection
        .select('text')
        .transition()
        .text(d => (d.children ? '-' : '+'));

      this.rootOfDown.eachBefore(d => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
      this.rootOfUp.eachBefore(d => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    } catch (error) {
      console.error('❌ Error in update function:', error);
      console.error('Stack trace:', error.stack);
      throw error;
    }
  }

  drawNodes(nodes, links, nodeClass, linkClass, markerId, source, transition) {
    try {
      const nodeSelection = this.gNodes
        .selectAll(`g.${nodeClass}`)
        .data(nodes, d => d.data.id);

      const nodeEnter = nodeSelection
        .enter()
        .append('g')
        .attr('class', nodeClass)
        .attr('transform', () => `translate(${source.x0},${source.y0})`)
        .attr('fill-opacity', 0)
        .attr('stroke-opacity', 0)
        .style('cursor', 'pointer');

      // Draw node rectangles and content
      this.drawNodeContent(nodeEnter, markerId);

      // Draw links
      const linkSelection = this.gLinks
        .selectAll(`path.${linkClass}`)
        .data(links, d => d.target.data.id);

      const linkEnter = linkSelection
        .enter()
        .append('path')
        .attr('class', linkClass)
        .attr('d', () => {
          const o = {
            source: { x: source.x0, y: source.y0 },
            target: { x: source.x0, y: source.y0 }
          };
          return this.drawLink(o);
        })
        .attr('fill', 'none')
        .attr('stroke', '#7A9EFF')
        .attr('stroke-width', 1)
        .attr('marker-end', `url(#${markerId})`);

      // Update positions
      const allNodes = nodeSelection.merge(nodeEnter);
      allNodes
        .attr('transform', d => `translate(${d.x},${d.y})`)
        .attr('fill-opacity', 1)
        .attr('stroke-opacity', 1);

      // Handle exits
      nodeSelection
        .exit()
        .transition(transition)
        .remove()
        .attr('transform', () => `translate(${source.x0},${source.y0})`)
        .attr('fill-opacity', 0)
        .attr('stroke-opacity', 0);

      linkSelection
        .merge(linkEnter)
        .transition(transition)
        .attr('d', this.drawLink);
      linkSelection
        .exit()
        .transition(transition)
        .remove()
        .attr('d', () => {
          const o = {
            source: { x: source.x, y: source.y },
            target: { x: source.x, y: source.y }
          };
          return this.drawLink(o);
        });
    } catch (error) {
      console.error(`❌ Error in drawNodes for ${nodeClass}:`, error);
      console.error('Stack trace:', error.stack);
      throw error;
    }
  }

  drawNodeContent(nodeEnter, markerId) {
    try {
      // Main rectangle
      nodeEnter
        .append('rect')
        .attr('width', d =>
          d.depth === 0 ? (d.data.name.length + 2) * 16 : this.config.rectWidth
        )
        .attr('height', d => (d.depth === 0 ? 40 : this.config.rectHeight))
        .attr('x', d =>
          d.depth === 0
            ? (-(d.data.name.length + 2) * 16) / 2
            : -this.config.rectWidth / 2
        )
        .attr('y', d => (d.depth === 0 ? -15 : -this.config.rectHeight / 2))
        .attr('rx', 5)
        .attr('stroke-width', 1)
        .attr('stroke', d => (d.depth === 0 ? '#5682ec' : '#7A9EFF'))
        .attr('fill', d => (d.depth === 0 ? '#7A9EFF' : '#FFFFFF'))
        .on('click', (e, d) => this.nodeClickEvent(e, d));

      // Main title
      nodeEnter
        .append('text')
        .attr('class', 'main-title')
        .attr('x', 0)
        .attr('y', d => (d.depth === 0 ? 10 : -14))
        .attr('text-anchor', 'middle')
        .text(d => {
          if (d.depth === 0) return d.data.name;
          return d.data.name.length > 11
            ? d.data.name.substring(0, 11)
            : d.data.name;
        })
        .attr('fill', d => (d.depth === 0 ? '#FFFFFF' : '#000000'))
        .style('font-size', d => (d.depth === 0 ? 16 : 14))
        .style('font-weight', 'bold')
        .append('svg:title')
        .text(d => d.data.name);

      // Sub title
      nodeEnter
        .append('text')
        .attr('class', 'sub-title')
        .attr('x', 0)
        .attr('y', 5)
        .attr('text-anchor', 'middle')
        .text(d => {
          if (d.depth !== 0) {
            const subTitle = d.data.name.substring(11);
            return subTitle.length > 10
              ? `${subTitle.substring(0, 10)}...`
              : subTitle;
          }
        })
        .style('font-size', 14)
        .style('font-weight', 'bold')
        .append('svg:title')
        .text(d => d.data.name);

      // Add different elements based on marker direction
      if (markerId === 'markerOfDown') {
        nodeEnter
          .append('svg:rect')
          .attr('x', '-84')
          .attr('y', '14')
          .attr('rx', 5)
          .attr('width', d => {
            if (d.depth !== 0 && d.data.rounds) {
              return this.config.rectWidth - 2;
            }
            return 0;
          })
          .attr('height', '20')
          .style('fill', '#E5F3FE');

        nodeEnter
          .append('text')
          .attr('x', '0')
          .attr('dy', '30')
          .attr('text-anchor', 'middle')
          .attr('class', 'linkname')
          .style('font-size', 12)
          .attr('fill', '#008BF8')
          .text(d => {
            if (d.depth !== 0 && d.data.rounds) {
              const str =
                t(
                  'components.ChatContent.components.ShareHolding.32b5ac53509870bc3a7d6079a7fdf0cf'
                ) + d.data.rounds;
              return str.length > 13 ? `${str.substring(0, 18)}..` : str;
            }
          });

        nodeEnter
          .append('text')
          .attr('class', 'percent')
          .attr('x', 15)
          .attr('y', -55)
          .text(d => {
            if (d.depth !== 0) {
              return d.data.percent;
            }
          })
          .attr('fill', '#0084FF')
          .style('font-size', 14);

        nodeEnter
          .append('svg:rect')
          .attr('x', '-40')
          .attr('y', '-70')
          .attr('rx', 2)
          .attr('width', d => {
            return d.depth !== 0 && d.data.percent ? 30 : 0;
          })
          .attr('height', d => {
            return d.depth !== 0 && d.data.percent ? 20 : 0;
          })
          .style('fill', '#EBF5FF');

        nodeEnter
          .append('text')
          .attr('x', '-37')
          .attr('dy', '-55')
          .attr('text-anchor', 'start')
          .style('fill', '#0084FF')
          .style('font-size', 12)
          .text(d => {
            return d.depth !== 0 && d.data.percent
              ? t(
                  'components.ChatContent.components.ShareHolding.569a97411ceed0865bf6f33647b61c2d'
                )
              : '';
          });

        nodeEnter
          .append('svg:rect')
          .attr('x', '10')
          .attr('y', '-48')
          .attr('rx', 2)
          .attr('width', d => {
            return d.depth !== 0 && d.data.off ? 45 : 0;
          })
          .attr('height', d => {
            return d.depth !== 0 && d.data.off ? 10 : 0;
          })
          .style('fill', '#fde2e2');

        nodeEnter
          .append('text')
          .attr('x', '15')
          .attr('dy', '-40')
          .attr('text-anchor', 'start')
          .style('fill', '#f89898')
          .style('font-size', 8)
          .text(d => {
            return d.depth !== 0 && d.data.off
              ? t(
                  'components.ChatContent.components.ShareHolding.add22c0eac5b6179061439f492b832a3'
                )
              : '';
          });
      } else {
        nodeEnter
          .append('text')
          .attr('class', 'percent')
          .attr('x', 12)
          .attr('y', 55)
          .text(d => {
            if (d.depth !== 0) {
              return d.data.percent;
            }
          })
          .attr('fill', d => {
            return d.data.controlling ? '#FA6B64' : '#000000';
          })
          .style('font-size', 14);

        nodeEnter
          .append('svg:rect')
          .attr('x', '50')
          .attr('y', '-55')
          .attr('rx', 2)
          .attr('width', d => {
            return d.depth !== 0 && d.data.address ? 28 : 0;
          })
          .attr('height', d => {
            return d.depth !== 0 && d.data.address ? 15 : 0;
          })
          .style('fill', ' #e1f3d8');

        nodeEnter
          .append('text')
          .attr('x', '53')
          .attr('dy', '-44')
          .attr('text-anchor', 'start')
          .style('fill', '#95d475')
          .style('font-size', 10)
          .text(d => {
            return d.depth !== 0 && d.data.address ? d.data.address : '';
          });

        nodeEnter
          .append('svg:rect')
          .attr('x', -50)
          .attr('y', -90)
          .attr('width', d => {
            return d.data.controlling ? 100 : 0;
          })
          .attr('height', d => {
            return d.data.controlling ? 40 : 0;
          })
          .attr('rx', 2)
          .style('stroke', d => {
            return d.data.controlling ? '#FA6B64' : '#F1B03A';
          })
          .style('fill', d => {
            return d.data.controlling ? '#FA6B64' : '#F1B03A';
          });

        nodeEnter
          .append('svg:path')
          .attr('fill', d => {
            return d.data.controlling ? '#FA6B64' : '#F1B03A';
          })
          .attr('d', d => {
            if (d.data.controlling) {
              return 'M0 -44 L-10 -54 L10 -54 Z';
            }
            return '';
          });

        nodeEnter
          .append('text')
          .attr('x', '0')
          .attr('dy', '-74')
          .attr('text-anchor', 'middle')
          .style('fill', '#fff')
          .style('font-size', 12)
          .text(d => {
            return d.data.controlling
              ? t(
                  'components.ChatContent.components.ShareHolding.caadf28ef96e7e5a9cae14103459b339'
                )
              : '';
          });

        nodeEnter
          .append('text')
          .attr('x', '0')
          .attr('dy', '-58')
          .attr('text-anchor', 'middle')
          .style('fill', '#fff')
          .style('font-size', 12)
          .text(d => {
            return d.data.controlling
              ? t(
                  'components.ChatContent.components.ShareHolding.1f45373f64677718b8656b64f0cbab67'
                )
              : '';
          });
      }

      // Add expand button
      const expandBtnG = nodeEnter
        .append('g')
        .attr('class', 'expandBtn')
        .attr(
          'transform',
          () =>
            `translate(0,${
              markerId === 'markerOfDown'
                ? this.config.rectHeight / 2
                : -this.config.rectHeight / 2
            })`
        )
        .style('display', d => {
          if (d.depth === 0 || !d._children) return 'none';
          return 'block';
        })
        .on('click', (e, d) => {
          if (d.children) {
            d._children = d.children;
            d.children = null;
          } else {
            d.children = d._children;
          }
          this.update(d);
        });

      expandBtnG
        .append('circle')
        .attr('r', 8)
        .attr('fill', '#7A9EFF')
        .attr('cy', markerId === 'markerOfDown' ? 8 : -8);

      expandBtnG
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('fill', '#ffffff')
        .attr('y', markerId === 'markerOfDown' ? 13 : -3)
        .style('font-size', 16)
        .text(d => (d.children ? '-' : '+'));
    } catch (error) {
      console.error(
        `❌ Error in drawNodeContent for markerId ${markerId}:`,
        error
      );
      console.error('Stack trace:', error.stack);
      throw error;
    }
  }

  // Right angle connection line
  // eslint-disable-next-line class-methods-use-this
  drawLink({ source, target }) {
    const halfDistance = (target.y - source.y) / 2;
    const halfY = source.y + halfDistance;
    return `M${source.x},${source.y} L${source.x},${halfY} ${target.x},${halfY} ${target.x},${target.y}`;
  }

  // Public methods
  expandAllNodes() {
    this.drawChart({ type: 'all' });
  }

  foldAllNodes() {
    this.drawChart({ type: 'fold' });
  }

  refreshChart() {
    this.drawChart({ type: 'all' });
  }

  nodeClickEvent(e, d) {
    console.log('node data:', d);
    // Dispatch custom event for external handling
    this.dispatchEvent(
      new CustomEvent('nodeClick', {
        detail: d,
        bubbles: true
      })
    );
  }

  // Getters and setters for data
  get data() {
    return this.getAttribute('data');
  }

  set data(value) {
    this.setAttribute('data', value);
  }

  get width() {
    return this.getAttribute('width');
  }

  set width(value) {
    this.setAttribute('width', value);
  }

  get height() {
    return this.getAttribute('height');
  }

  set height(value) {
    this.setAttribute('height', value);
  }
}

customElements.define('shareholding-component', ShareHoldingComponent);
